/**
 * Signed URL Service - Secure File Access
 * Generates time-limited signed URLs for Firebase Storage files
 * 
 * Environment Configuration Required:
 * - REACT_APP_SERVER_URL: Server endpoint for signed URL generation (e.g., Firebase Cloud Functions)
 * 
 * Server-side Requirements:
 * - Firebase Admin SDK configured with service account credentials
 * - /api/signed-url endpoint implemented (see server.js)
 */

import { getDownloadURL } from 'firebase/storage';
import { ref as storageRef } from 'firebase/storage';
import { storage } from '../config/firebase';

class SignedUrlService {
  constructor() {
    this.cache = new Map();
    this.defaultExpiryMinutes = 15; // 15 minutes default
    this.serverEndpoint = process.env.REACT_APP_SERVER_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net';
  }

  /**
   * Generate a signed URL for a storage path
   * @param {string} storagePath - Full storage path (e.g., 'documents/orgId/file.xlsx')
   * @param {number} expiryMinutes - Expiry time in minutes (default: 15)
   * @returns {Promise<string>} - Signed URL
   */
  async generateSignedUrl(storagePath, expiryMinutes = this.defaultExpiryMinutes) {
    try {
      // Check cache first
      const cacheKey = `${storagePath}_${expiryMinutes}`;
      const cachedUrl = this.getCachedUrl(cacheKey);
      if (cachedUrl) {
        console.log('✅ Using cached signed URL for:', storagePath);
        return cachedUrl;
      }

      console.log('🔐 Generating signed URL for:', storagePath);

      // Try server-side signed URL generation first
      try {
        const signedUrl = await this.generateServerSignedUrl(storagePath, expiryMinutes);
        if (signedUrl) {
          this.cacheUrl(cacheKey, signedUrl, expiryMinutes);
          console.log('✅ Server-generated signed URL created for:', storagePath);
          return signedUrl;
        }
      } catch (serverError) {
        console.warn('⚠️ Server-side signed URL generation failed:', serverError.message);
      }

      // Fallback to standard download URL
      console.log('🔄 Falling back to standard download URL for:', storagePath);
      const fallbackUrl = await this.getFallbackUrl(storagePath);
      
      // Cache fallback URL for shorter time (5 minutes)
      this.cacheUrl(cacheKey, fallbackUrl, 5);
      
      return fallbackUrl;

    } catch (error) {
      console.error('❌ Error generating signed URL:', error);
      throw new Error(`Failed to generate signed URL for ${storagePath}: ${error.message}`);
    }
  }

  /**
   * Generate signed URL via server endpoint
   * @param {string} storagePath - Storage path
   * @param {number} expiryMinutes - Expiry time
   * @returns {Promise<string>} - Signed URL
   */
  async generateServerSignedUrl(storagePath, expiryMinutes) {
    const response = await fetch(`${this.serverEndpoint}/api/signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await this.getAuthToken()}`,
      },
      body: JSON.stringify({
        storagePath,
        expiryMinutes
      })
    });

    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.signedUrl;
  }

  /**
   * Get fallback download URL using Firebase client SDK
   * @param {string} storagePath - Storage path
   * @returns {Promise<string>} - Download URL
   */
  async getFallbackUrl(storagePath) {
    const fileRef = storageRef(storage, storagePath);
    return await getDownloadURL(fileRef);
  }

  /**
   * Get current user's auth token
   * @returns {Promise<string>} - Auth token
   */
  async getAuthToken() {
    // Get current user's ID token
    const { currentUser } = await import('../contexts/AuthContext');
    if (currentUser?.getIdToken) {
      return await currentUser.getIdToken();
    }
    
    // Fallback: get from Firebase auth directly
    const { auth } = await import('../config/firebase');
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    
    throw new Error('No authenticated user found');
  }

  /**
   * Cache URL with expiry
   * @param {string} key - Cache key
   * @param {string} url - URL to cache
   * @param {number} expiryMinutes - Expiry time
   */
  cacheUrl(key, url, expiryMinutes) {
    const expiryTime = Date.now() + (expiryMinutes * 60 * 1000);
    this.cache.set(key, {
      url,
      expiryTime
    });

    // Clean up expired entries periodically
    this.cleanupCache();
  }

  /**
   * Get cached URL if not expired
   * @param {string} key - Cache key
   * @returns {string|null} - Cached URL or null
   */
  getCachedUrl(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() > cached.expiryTime) {
      this.cache.delete(key);
      return null;
    }

    return cached.url;
  }

  /**
   * Clean up expired cache entries
   */
  cleanupCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiryTime) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cached URLs
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get stats about cache usage
   * @returns {object} - Cache statistics
   */
  getCacheStats() {
    const totalEntries = this.cache.size;
    const now = Date.now();
    let expiredEntries = 0;

    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiryTime) {
        expiredEntries++;
      }
    }

    return {
      totalEntries,
      expiredEntries,
      activeEntries: totalEntries - expiredEntries
    };
  }
}

// Export singleton instance
export const signedUrlService = new SignedUrlService();
export default signedUrlService; 